import { MeetingReferenceDetails } from "../../components/meetings/MeetingReferenceDetails";
import { Link } from "react-router-dom";
import type { WorkspaceReference } from "../../api/references.api";
import { useReferenceResolution } from "../../components/references/ReferenceList";
import { noteReferenceToken } from "../../components/references/NoteComposer";
import { Fragment } from "react";

/** Deliberately small text grammar: no HTML, embeds or executable links. */
export function NoteBody({
  body,
  references = [],
  onNavigate,
  showDetails = false,
}: {
  body: string;
  references?: WorkspaceReference[];
  showDetails?: boolean;
  onNavigate?: (href: string) => void;
}) {
  const resolved = useReferenceResolution(references);
  const linked = (text: string) =>
    text.split(/(#[[][A-Z_]+:[a-zA-Z0-9-]+\])/g).map((part, index) => {
      const position = references.findIndex(
        (r) => noteReferenceToken(r) === part,
      );
      if (position < 0) return <Fragment key={index}>{part}</Fragment>;
      const item = resolved.items?.[position];
      const link =
        item?.available &&
        item.href.startsWith("/") &&
        !item.href.startsWith("//") ? (
          onNavigate ? (
            <a
              key={index}
              href={item.href}
              onClick={(e) => {
                if (
                  e.button !== 0 ||
                  e.ctrlKey ||
                  e.metaKey ||
                  e.shiftKey ||
                  e.altKey
                )
                  return;
                e.preventDefault();
                onNavigate(item.href);
              }}
            >
              #{item.label}
            </a>
          ) : (
            <Link key={index} to={item.href}>
              #{item.label}
            </Link>
          )
        ) : (
          <span key={index}>
            {resolved.pending
              ? "Vérification de l’objet…"
              : resolved.error
                ? "Vérification de l’objet impossible"
                : item
                  ? "Objet Permio indisponible"
                  : "Objet à revérifier"}
            {!resolved.pending && !item && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  resolved.retry();
                }}
              >
                Réessayer
              </button>
            )}
          </span>
        );
      return (
        <Fragment key={index}>
          {link}
          {showDetails &&
            item?.available &&
            (references[position].entityType === "MEETING" ? (
              <MeetingReferenceDetails id={references[position].entityId} />
            ) : (
              <span style={{ display: "block", fontSize: 12 }}>
                {item.fields?.map((field) => (
                  <span style={{ display: "block" }} key={field.label}>
                    {field.label} : {field.value}
                  </span>
                ))}
              </span>
            ))}
        </Fragment>
      );
    });
  const inline = (text: string) =>
    text
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{linked(part.slice(2, -2))}</strong>
        ) : (
          <Fragment key={i}>{linked(part)}</Fragment>
        ),
      );
  return (
    <div className="workspace-description">
      {body.split("\n").map((line, i) => {
        const task = /^- \[([ xX])\] (.*)$/.exec(line);
        if (task)
          return (
            <div key={i}>
              <input
                type="checkbox"
                checked={task[1] !== " "}
                disabled
                aria-label={task[2]}
              />{" "}
              {inline(task[2])}
            </div>
          );
        if (line.startsWith("## "))
          return <h3 key={i}>{inline(line.slice(3))}</h3>;
        if (line.startsWith("- "))
          return <div key={i}>• {inline(line.slice(2))}</div>;
        return <div key={i}>{inline(line) || <br />}</div>;
      })}
    </div>
  );
}
